import { css } from 'aphrodite';
import * as React from 'react';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { PlusIconThin } from '../../../../../web/components/svgs/icons/PlusIconThin';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { charmGray } from '../../../../../web/styles/colors';
import { avatarUploaderStyles } from './AvatarImageUploader.styles';

interface AvatarImageUploaderProps {
	previewUrl: string | null;
	onImageChange: (file: File | null) => void;
	disabled?: boolean;
	isUploading?: boolean;
}

export function AvatarImageUploader({
	previewUrl,
	onImageChange,
	disabled = false,
	isUploading = false,
}: AvatarImageUploaderProps) {
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = React.useState(false);

	const isDisabled = disabled || isUploading;

	const openFileDialog = () => {
		if (!isDisabled) {
			fileInputRef.current?.click();
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			onImageChange(file);
		}
		// Reset input value to allow re-selecting the same file
		event.target.value = '';
	};

	const handleDragEnter = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDisabled) {
			setIsDragging(true);
		}
	};

	const handleDragLeave = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
	};

	const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);

		if (isDisabled) return;

		const file = e.dataTransfer.files?.[0];
		if (file && file.type.startsWith('image/')) {
			onImageChange(file);
		}
	};

	const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		onImageChange(null);
	};

	return (
		<div className={css(avatarUploaderStyles.container)}>
			<button
				aria-label={previewUrl ? 'Change image' : 'Upload image'}
				className={css(
					avatarUploaderStyles.dropArea,
					isDragging && avatarUploaderStyles.dropAreaDragging,
					previewUrl && avatarUploaderStyles.dropAreaWithImage,
					isDisabled && avatarUploaderStyles.dropAreaDisabled
				)}
				onClick={openFileDialog}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				type='button'
				disabled={isDisabled}
			>
				{previewUrl ? (
					<img alt='Uploaded avatar' className={css(avatarUploaderStyles.previewImage)} src={previewUrl} />
				) : (
					<div aria-hidden='true' className={css(avatarUploaderStyles.iconContainer)}>
						<PlusIconThin height={20} width={20} fillColor={charmGray} />
					</div>
				)}
				{isUploading && (
					<div className={css(avatarUploaderStyles.uploadingOverlay)}>
						<LoadingSpinner type='small' />
					</div>
				)}
			</button>
			{previewUrl && !isDisabled && (
				<button
					aria-label='Remove image'
					className={css(avatarUploaderStyles.removeButton)}
					onClick={handleRemove}
					type='button'
				>
					<XIcon width={10} height={10} />
				</button>
			)}
			<input
				ref={fileInputRef}
				type='file'
				accept='image/*'
				onChange={handleFileChange}
				className={css(avatarUploaderStyles.hiddenInput)}
				tabIndex={-1}
				aria-label='Upload image file'
			/>
		</div>
	);
}
